import { useState, useEffect, useCallback } from "react";
import { Search, Loader2, Printer, User } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { useOssAuth } from "../../context/OssAuthContext";
import { openPrintPlaceholderWindow, printPdfBlob } from "../../utils/printPdfBlob";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Oss118Dialog({ open, onClose }) {
  const { token } = useOssAuth();
  const [ospiti, setOspiti] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [query, setQuery] = useState("");
  const [generatingId, setGeneratingId] = useState(null);

  const loadOspiti = useCallback(() => {
    if (!token) return;
    setLoadingList(true);
    axios
      .get(`${API_URL}/api/oss/me/fascicoli`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => setOspiti(data || []))
      .catch(() => toast.error("Errore nel caricamento degli ospiti"))
      .finally(() => setLoadingList(false));
  }, [token]);

  useEffect(() => {
    if (open) {
      setQuery("");
      loadOspiti();
    }
  }, [open, loadOspiti]);

  const filtered = ospiti.filter((o) => {
    const q = query.toLowerCase();
    if (!q) return true;
    return (
      (o.nome || "").toLowerCase().includes(q) ||
      (o.cognome || "").toLowerCase().includes(q) ||
      (o.nome_completo || "").toLowerCase().includes(q) ||
      (o.camera || "").toLowerCase().includes(q)
    );
  });

  const handlePrint = async (ospite) => {
    const id = ospite.id || ospite._id;
    if (!id || !token) return;
    const title = `118 — ${ospite.cognome || ""} ${ospite.nome || ""}`.trim();
    const printWindow = openPrintPlaceholderWindow(title || "Scheda emergenza 118");
    if (!printWindow) {
      toast.error("Popup bloccato dal browser. Abilita i popup per stampare.");
      return;
    }
    setGeneratingId(id);
    try {
      const response = await axios.get(
        `${API_URL}/api/oss/me/fascicoli/${id}/118.pdf`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" }
      );
      const result = printPdfBlob(response.data, { title: title || "Scheda emergenza 118", printWindow });
      if (!result.ok) {
        printWindow.close();
        toast.error("Popup bloccato dal browser. Abilita i popup per stampare.");
        return;
      }
      toast.success("PDF pronto — dialogo di stampa aperto");
      onClose();
    } catch {
      try {
        printWindow.close();
      } catch {
        /* ignore */
      }
      toast.error("Errore nella generazione del PDF");
    } finally {
      setGeneratingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg w-full max-h-[85dvh] flex flex-col p-0 gap-0">
        {/* Header */}
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-100 shrink-0">
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <span className="text-2xl font-black">118</span>
            <span className="text-lg font-bold">Emergenza — Seleziona ospite</span>
          </DialogTitle>
          <p className="text-sm text-slate-500 mt-1">
            Seleziona l'ospite per stampare subito la scheda da consegnare ai soccorsi.
          </p>
        </DialogHeader>

        {/* Search */}
        <div className="px-5 py-3 border-b border-slate-100 shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Cerca per nome, cognome o camera…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9 h-10"
              autoFocus
            />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-3 py-2">
          {loadingList ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-red-500" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-slate-400 py-10 text-sm">
              {query ? "Nessun ospite trovato" : "Nessun ospite disponibile"}
            </p>
          ) : (
            <ul className="space-y-1">
              {filtered.map((ospite) => {
                const id = ospite.id || ospite._id;
                const isGenerating = generatingId === id;
                return (
                  <li key={id}>
                    <button
                      className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 active:bg-slate-100 transition-colors text-left touch-manipulation disabled:opacity-60"
                      onClick={() => handlePrint(ospite)}
                      disabled={isGenerating}
                    >
                      {/* Photo / initials */}
                      <div className="w-10 h-10 rounded-full bg-slate-200 overflow-hidden flex items-center justify-center shrink-0">
                        {ospite.foto_url ? (
                          <img
                            src={ospite.foto_url}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <User className="w-5 h-5 text-slate-400" />
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-800 truncate">
                          {ospite.cognome} {ospite.nome}
                        </p>
                        <p className="text-xs text-slate-500">
                          {ospite.camera && ospite.camera !== "N/A"
                            ? `Camera ${ospite.camera}`
                            : ""}
                          {ospite.diagnosi_principale
                            ? `${ospite.camera && ospite.camera !== "N/A" ? " • " : ""}${ospite.diagnosi_principale}`
                            : ""}
                        </p>
                      </div>

                      {/* Action */}
                      <div className="shrink-0">
                        {isGenerating ? (
                          <Loader2 className="w-5 h-5 animate-spin text-red-500" />
                        ) : (
                          <Printer className="w-5 h-5 text-red-500" />
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
